import { Base, withBreakpointObserver } from '@studiometa/js-toolkit';
import { gsap } from 'gsap';

/**
 * @typedef {{
 *   $refs: {
 *     items: HTMLElement[],
 *     pictures: HTMLElement[],
 *   }
 * }} GalleryProps
 */

/**
 * Gallery component.
 * @augments {Base<GalleryProps>}
 */
export default class Gallery extends withBreakpointObserver(Base) {
  /**
   * Class config.
   * @type {import('@studiometa/js-toolkit').BaseConfig}
   */
  static config = {
    name: 'Gallery',
    refs: ['items[]', 'pictures[]'],
    options: {
      inactiveBreakpoints: {
        type: String,
        default: 'xxs xs s',
      },
    },
  };

  /** @type {number} */
  activeIndex = -1;

  /** @type {{ x: number, y: number }} */
  mouse = { x: 0, y: 0 };

  /**
   *
   */
  mounted() {
    this.activeIndex = 0;

    if (window.__pageTransitioning) {
      document.addEventListener('page:enter', () => this.intro(), { once: true });
    } else if (window.__loaderComplete) {
      this.intro();
    } else {
      document.addEventListener('loader:complete', () => this.intro(), { once: true });
    }
  }

  /**
   * Play the intro animation.
   */
  intro() {
    const tl = gsap.timeline();

    tl.fromTo(
      this.$refs.pictures[0],
      { opacity: 0 },
      { opacity: 1, duration: 0.8, ease: 'power2.out' },
    );

    tl.fromTo(
      this.$refs.items,
      { opacity: 0, y: 30 },
      {
        opacity: (i) => (i === 0 ? 1 : 0.5),
        clearProps: 'transform',
        y: 0,
        duration: 0.6,
        ease: 'power2.out',
        stagger: 0.1,
      },
      '-=0.4',
    );
  }

  /**
   * Track mouse position.
   * @param {{ event: MouseEvent }} args
   */
  onMousemove({ event }) {
    this.mouse.x = event.clientX;
    this.mouse.y = event.clientY;
  }

  /**
   * Re-evaluate hover on scroll.
   */
  scrolled() {
    if (!this.mouse.x && !this.mouse.y) return;

    const el = document.elementFromPoint(this.mouse.x, this.mouse.y);
    if (!el) return;

    const index = this.$refs.items.findIndex((item) => item === el || item.contains(el));

    if (index !== -1 && index !== this.activeIndex) {
      this.activate(index);
    }
  }

  /**
   * Handle mouseenter on an item ref.
   * @param {{ index: number }} args
   */
  onItemsMouseenter({ index }) {
    if (index !== this.activeIndex) {
      this.activate(index);
    }
  }

  /**
   * Activate item and picture at the given index.
   * @param {number} index
   */
  activate(index) {
    const prevIndex = this.activeIndex;
    this.activeIndex = index;

    this.$refs.items.forEach((item, i) => {
      item.classList.toggle('is-active', i === index);
      gsap.to(item, {
        opacity: i === index ? 1 : 0.5,
        duration: 0.3,
        overwrite: true,
      });
    });

    if (prevIndex !== -1 && this.$refs.pictures[prevIndex]) {
      gsap.to(this.$refs.pictures[prevIndex], {
        opacity: 0,
        scale: 1.01,
        duration: 0.6,
        overwrite: true,
      });
    }

    if (this.$refs.pictures[index]) {
      gsap.to(this.$refs.pictures[index], {
        opacity: 1,
        scale: 1,
        duration: 0.6,
        overwrite: true,
      });
    }
  }

  /**
   * Reset state on mouseleave.
   */
  onMouseleave() {
    if (this.activeIndex !== -1 && this.$refs.pictures[this.activeIndex]) {
      gsap.to(this.$refs.pictures[this.activeIndex], {
        opacity: 0,
        duration: 0.6,
        overwrite: true,
      });
    }
    this.activeIndex = -1;

    this.$refs.items.forEach((item) => {
      item.classList.remove('is-active');
      gsap.to(item, {
        opacity: 0.5,
        duration: 0.3,
        overwrite: true,
      });
    });
  }
}
